import {useCallback, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDeleteChatDocument, useUploadChatDocument} from "../../api/chat/chatQueries";
import {ChatDocument, PendingChatUpload} from "../../util/types";

const maximumAttachmentCount = 3;
const maximumAttachmentSizeInBytes = 10 * 1024 * 1024;
const maximumAttachmentSizeInMegabytes = 10;
const supportedAttachmentExtensions = [".pdf", ".txt", ".doc", ".docx", ".md", ".pptx"] as const;

type Props = {
    attachmentKey: string;
    attachments: PendingChatUpload[];
    chatId: string | undefined;
    documents: ChatDocument[];
    onAttachmentsChange: (attachmentKey: string, attachments: PendingChatUpload[]) => void;
    onChatCreated: (chatId: string) => void;
};

export type AttachmentNotice = {
    message: string;
    severity: "error" | "info";
};

type Result = {
    attachmentNotice: AttachmentNotice | undefined;
    clearAttachmentNotice: () => void;
    handleAttachmentFiles: (selectedFiles: FileList | null) => Promise<void>;
    handleRemoveDocument: (documentId: string) => Promise<void>;
    maximumAttachmentCount: number;
    maximumAttachmentSizeInMegabytes: number;
};

const useChatAttachments = ({
                                attachmentKey,
                                attachments,
                                chatId,
                                documents,
                                onAttachmentsChange,
                                onChatCreated,
                            }: Props): Result => {
    const [attachmentNotice, setAttachmentNotice] = useState<AttachmentNotice | undefined>(undefined);
    const {t: translate} = useTranslation();
    const uploadChatDocumentMutation = useUploadChatDocument();
    const deleteChatDocumentMutation = useDeleteChatDocument();

    const createAttachmentId = useCallback((file: File) => {
        if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
            return crypto.randomUUID();
        }

        return `${file.name}-${file.size}-${file.lastModified}-${Date.now()}`;
    }, []);

    const isSupportedAttachmentFile = useCallback((file: File) => {
        const lowercaseFilename = file.name.toLowerCase();

        return supportedAttachmentExtensions.some((extension) => lowercaseFilename.endsWith(extension));
    }, []);

    const handleAttachmentFiles = useCallback(async(selectedFiles: FileList | null) => {
        if (!selectedFiles?.length) {
            return;
        }

        const nextFiles = Array.from(selectedFiles);
        const knownFilenames = new Set([
            ...attachments.map((attachment) => attachment.filename),
            ...documents.map((document) => document.filename),
        ]);
        const duplicateFiles: File[] = [];
        const uniqueFiles = nextFiles.filter((file) => {
            if (knownFilenames.has(file.name)) {
                duplicateFiles.push(file);
                return false;
            }

            knownFilenames.add(file.name);
            return true;
        });

        const duplicateNotice = duplicateFiles.length === 1
            ? {
                message: translate("chat.attachments.errors.duplicateFile", {fileName: duplicateFiles[0].name}),
                severity: "info" as const,
            }
            : duplicateFiles.length > 1
                ? {
                    message: translate("chat.attachments.errors.duplicateFiles", {count: duplicateFiles.length}),
                    severity: "info" as const,
                }
                : undefined;

        if (uniqueFiles.length === 0) {
            if (duplicateNotice) {
                setAttachmentNotice(duplicateNotice);
            }
            return;
        }

        if (attachments.length + documents.length + uniqueFiles.length > maximumAttachmentCount) {
            setAttachmentNotice({
                message: translate("chat.attachments.errors.tooManyFiles", {maxFiles: maximumAttachmentCount}),
                severity: "error",
            });
            return;
        }

        const oversizedFile = uniqueFiles.find((file) => file.size > maximumAttachmentSizeInBytes);
        if (oversizedFile) {
            setAttachmentNotice({
                message: translate("chat.attachments.errors.fileTooLarge", {
                    fileName: oversizedFile.name,
                    maxSizeMb: maximumAttachmentSizeInMegabytes,
                }),
                severity: "error",
            });
            return;
        }

        const unsupportedFile = uniqueFiles.find((file) => !isSupportedAttachmentFile(file));
        if (unsupportedFile) {
            setAttachmentNotice({
                message: translate("chat.attachments.errors.unsupportedType", {fileName: unsupportedFile.name}),
                severity: "error",
            });
            return;
        }

        const newPendingUploads = uniqueFiles.map((file) => ({
            id: createAttachmentId(file),
            filename: file.name,
        }));
        let pendingUploads = [...attachments, ...newPendingUploads];
        let activeAttachmentKey = attachmentKey;
        let resolvedChatId = chatId;
        onAttachmentsChange(activeAttachmentKey, pendingUploads);
        setAttachmentNotice(duplicateNotice);

        for (let index = 0; index < uniqueFiles.length; index += 1) {
            const file = uniqueFiles[index];
            const pendingUpload = newPendingUploads[index];

            try {
                const result = await uploadChatDocumentMutation.mutateAsync({
                    chatId: resolvedChatId,
                    file,
                });
                if (!resolvedChatId) {
                    resolvedChatId = result.chatId;
                    activeAttachmentKey = result.chatId;
                    onChatCreated(result.chatId);
                }
            } catch (error) {
                setAttachmentNotice({
                    message: error instanceof Error ? error.message : translate("errors.TECHNICAL_ERROR"),
                    severity: "error",
                });
            }

            pendingUploads = pendingUploads.filter((attachment) => attachment.id !== pendingUpload.id);
            onAttachmentsChange(activeAttachmentKey, pendingUploads);
        }
    }, [
        attachmentKey,
        attachments,
        chatId,
        createAttachmentId,
        documents,
        isSupportedAttachmentFile,
        onAttachmentsChange,
        onChatCreated,
        translate,
        uploadChatDocumentMutation,
    ]);

    const handleRemoveDocument = useCallback(async(documentId: string) => {
        if (!chatId) {
            return;
        }

        setAttachmentNotice(undefined);

        try {
            await deleteChatDocumentMutation.mutateAsync({chatId, documentId});
        } catch (error) {
            setAttachmentNotice({
                message: error instanceof Error ? error.message : translate("errors.TECHNICAL_ERROR"),
                severity: "error",
            });
        }
    }, [chatId, deleteChatDocumentMutation, translate]);

    const clearAttachmentNotice = useCallback(() => {
        setAttachmentNotice(undefined);
    }, []);

    return {
        attachmentNotice,
        clearAttachmentNotice,
        handleAttachmentFiles,
        handleRemoveDocument,
        maximumAttachmentCount,
        maximumAttachmentSizeInMegabytes,
    };
};

export default useChatAttachments;
